import { useEffect, useState } from "react";
import { FloorPriceResponse, PetWithPrice } from "../../../common/types";
import { PetStatCard } from "../cards/PetStatCard";
import { PetTimerCard } from "../cards/PetTimerCard";
import { formatPrice, formatMinSec } from "../../helpers/helpers";

export function PetStore({
  topListings,
  potionStats,
  isPhone,
  remainingTime,
  walletAddress,
  isDarkMode,
}: {
  topListings: PetWithPrice[];
  potionStats: FloorPriceResponse;
  isPhone: boolean;
  remainingTime: number;
  walletAddress: string;
  isDarkMode: boolean;
}) {
  const [sortedPets, setSortedPets] = useState<PetWithPrice[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [startX, setStartX] = useState<number | null>(null);
  const [isStatsSelected, setIsStatsSelected] = useState(false);
  const [floorPrice, setFloorPrice] = useState<number | null>(null);

  useEffect(() => {
    const uniqueListings = topListings.filter(
      (pet, index, self) => index === self.findIndex((p) => p.petId === pet.petId),
    );
    const sorted = [...uniqueListings].sort((a, b) => (b.priceInEth || 0) - (a.priceInEth || 0));
    setSortedPets(sorted);
  }, [topListings]);

  useEffect(() => {
    if (potionStats && potionStats.floor_price) {
      setFloorPrice(potionStats.floor_price);
    }
  }, [potionStats]);

  const handleTouchStart = (e: React.TouchEvent) => {
    if (isPhone) setStartX(e.touches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (isPhone && startX !== null) {
      const diffX = startX - e.touches[0].clientX;

      if (diffX > 50) {
        handleNext();
        setStartX(null);
      }

      if (diffX < -50) {
        handlePrev();
        setStartX(null);
      }
    }
  };

  const handleTouchEnd = () => {
    if (isPhone) setStartX(null);
  };

  const handlePrev = () => {
    setCurrentIndex((prevIndex) => (prevIndex > 0 ? prevIndex - 1 : sortedPets.length - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prevIndex) => (prevIndex < sortedPets.length - 1 ? prevIndex + 1 : 0));
  };

  const currentPet = sortedPets[currentIndex];

  return (
    <div className="flex h-full w-full flex-col">
      <div className="mr-6 flex justify-center">
        <div
          className={`mb-4 ml-2 mt-4 flex w-72 flex-row justify-center rounded-lg border-[1.5px] px-2 py-1 text-lg ${isDarkMode ? "border-darkgreen bg-darkblue text-white [box-shadow:inset_0-2px_#37b764]" : "border-brown bg-white text-brown [box-shadow:inset_0_-2px_#342e2e]"}`}
        >
          <div className="flex flex-row"> Shop Refresh </div>
          <img
            src={isDarkMode ? "/imgs/storeDark.svg" : "/imgs/store.svg"}
            alt="Next"
            className="ml-2 mt-[.38rem] h-4 w-4"
          />
          <div className="ml-2 flex max-w-20 flex-row"> {formatMinSec(remainingTime)} </div>
        </div>
      </div>

      <div className="flex flex-row justify-center">
        <div
          className={`mb-4 ml-8 mt-4 flex w-60 flex-row justify-center rounded-lg border-[1.5px] border-brown px-2 py-1 text-lg ${isDarkMode ? "border-darkgreen bg-darkblue text-white [box-shadow:inset_0-2px_#37b764]" : "border-brown bg-white text-brown [box-shadow:inset_0_-2px_#342e2e]"}`}
        >
          <img
            src={isDarkMode ? "/imgs/darkpaw.svg" : "/imgs/paw.svg"}
            alt=""
            className="mr-2 mt-[0.2rem] h-5 w-5"
          />
          Adoption Center
          <img
            src={isDarkMode ? "/imgs/darkpaw.svg" : "/imgs/paw.svg"}
            alt=""
            className="ml-2 mt-[0.2rem] h-5 w-5"
          />
        </div>
        <div
          className={`relative ml-2 mt-6 h-6 w-10 cursor-pointer rounded-full border-2
            ${isDarkMode ? "border-darkgreen" : "border-brown"}
            ${
              isStatsSelected
                ? isDarkMode
                  ? "bg-darkblue"
                  : "bg-green"
                : isDarkMode
                  ? "bg-darkblue"
                  : "bg-white"
            }`}
          onClick={() => setIsStatsSelected((prev) => !prev)}
        >
          <div
            className={`m-[.135rem] h-4 w-4 rounded-full ${isDarkMode ? "bg-darkgreen" : "bg-brown"} ${
              isStatsSelected ? "translate-x-full transform" : ""
            }`}
          ></div>
        </div>
      </div>

      <div
        className="relative mx-auto w-full max-w-lg"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {sortedPets.length > 0 ? (
          <div className="flex items-center justify-center">
            {sortedPets.length > 1 && !isPhone && (
              <button
                className={`absolute left-0 z-10 mb-20 rounded-full border-2 p-2 ${isDarkMode ? "hover-darkblack border-darkgreen bg-darkblue" : "border-brown bg-white hover:bg-green"}`}
                onClick={handlePrev}
              >
                <img
                  src={isDarkMode ? "/imgs/playDark.svg" : "/imgs/play.svg"}
                  alt="Previous"
                  className="h-6 w-6 rotate-180 transform"
                />
              </button>
            )}

            <div className="flex flex-col justify-center">
              {currentPet &&
                (isStatsSelected ? (
                  <PetStatCard isDarkMode={isDarkMode} pet={currentPet} />
                ) : (
                  <PetTimerCard
                    isDarkMode={isDarkMode}
                    pet={currentPet}
                    walletAddress={walletAddress}
                  />
                ))}
              <a
                href={`https://opensea.io/assets/base/0x5b51cf49cb48617084ef35e7c7d7a21914769ff1/${currentPet.petId}`}
                target="_blank"
                rel="noreferrer"
              >
                <div
                  className={`mb-4 ml-[4.2rem] mt-4 flex w-60 flex-row justify-center rounded-lg border-[1.5px] px-2 py-1 text-lg ${isDarkMode ? "border-darkgreen bg-darkblue text-white [box-shadow:inset_0_-2px_#37b764] hover:bg-darkblack" : "border-brown bg-white [box-shadow:inset_0_-2px_#342e2e] hover:bg-green"} `}
                >
                  Adopt
                  <img
                    src={isDarkMode ? "/imgs/darkpaw.svg" : "/imgs/paw.svg"}
                    alt=""
                    className="ml-2 mr-2 mt-[0.2rem] h-5 w-5"
                  />
                  {currentPet?.priceInEth ? formatPrice(currentPet.priceInEth) : "N/A"} ETH
                </div>
              </a>
            </div>

            {sortedPets.length > 1 && !isPhone && (
              <button
                className={`absolute right-0 z-10 mb-20 rounded-full border-2 p-2 ${isDarkMode ? "border-darkgreen bg-darkblue hover:bg-darkblack" : "border-brown bg-white hover:bg-green"}`}
                onClick={handleNext}
              >
                <img
                  src={isDarkMode ? "/imgs/playDark.svg" : "/imgs/play.svg"}
                  alt=""
                  className="h-6 w-6"
                />
              </button>
            )}
          </div>
        ) : (
          <div className="mr-4 flex items-center justify-center">
            <div
              className={`text-md rounded-lg border-2 ${isDarkMode ? "border-darkgreen bg-darkblue text-white [box-shadow:inset_0_-2px_#37b764]" : "border-brown bg-white text-brown [box-shadow:inset_0_-2px_#342e2e]"} w-[16.5rem]`}
            >
              <img src="/imgs/fp_penguin_idle.png" alt=" " className="ml-14 h-32 w-36" />
              <div className="text-md mb-2 text-center"> No pets up for adoption </div>
            </div>
          </div>
        )}
      </div>

      <div className={`mt-8 border-b-2 ${isDarkMode ? "border-darkgreen" : "border-brown"}`}></div>

      <div className="flex flex-row justify-center">
        <div
          className={`mb-4 mr-4 mt-8 flex w-52 flex-row justify-center rounded-lg border-[1.5px] px-2 py-1 text-lg ${isDarkMode ? "border-darkgreen bg-darkblue text-white [box-shadow:inset_0_-2px_#37b764]" : "border-brown bg-white [box-shadow:inset_0_-2px_#342e2e]"}`}
        >
          <img
            src={isDarkMode ? "/imgs/flaskDark.svg" : "/imgs/flask.svg"}
            alt=""
            className="mr-2 mt-[0.2rem] h-5 w-5"
          />
          Potion Market
          <img
            src={isDarkMode ? "/imgs/flaskDark.svg" : "/imgs/flask.svg"}
            alt=""
            className="ml-2 mt-[0.2rem] h-5 w-5"
          />
        </div>
      </div>

      <div className="relative mx-auto w-full max-w-lg">
        <div className="mr-4 flex items-center justify-center">
          <div
            className={`mb-4 mt-2 flex w-40 items-center justify-center rounded-lg border-[1.5px] px-2 py-1 ${isDarkMode ? "border-darkgreen bg-darkblue [box-shadow:inset_0_-2px_#37b764]" : "border-brown bg-white [box-shadow:inset_0_-2px_#342e2e]"}`}
          >
            <img src="/imgs/potion.png" alt="" className="h-32 w-36" />
          </div>
        </div>
        <div className="mr-20 flex flex-col items-center justify-center">
          <a
            href={`https://opensea.io/assets/base/0xd63b06d0a14a656731d338eb01c31dc9716c1d64/1`}
            target="_blank"
            rel="noreferrer"
          >
            <div
              className={`ml-[4.2rem] mt-2 flex w-60 flex-row justify-center rounded-lg border-[1.5px] px-2 py-1 text-lg ${isDarkMode ? "border-darkgreen bg-darkblue text-white [box-shadow:inset_0_-2px_#37b764] hover:bg-darkblack" : "border-brown bg-white [box-shadow:inset_0_-2px_#342e2e] hover:bg-green"} `}
            >
              Buy{" "}
              <img
                src={isDarkMode ? "/imgs/flaskDark.svg" : "/imgs/flask.svg"}
                alt=""
                className="ml-2 mr-2 mt-[0.3rem] h-4 w-4"
              />{" "}
              {floorPrice ? `${formatPrice(floorPrice)} ETH` : " "}
            </div>
          </a>
        </div>
      </div>
    </div>
  );
}
